import { useTheme } from '@context/ThemeContext';
import { useAnalyticsConsent } from '@context/AnalyticsConsentContext';
import { Layout } from 'antd';
import { Fragment, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Footer.module.css';

const { Footer: AntFooter } = Layout;

const LANDING_BASE_URL = 'https://bundledrop.app';

const productLinks = [
  { labelKey: 'footer.links.docs', href: `${LANDING_BASE_URL}/docs` },
  { labelKey: 'footer.links.api', href: `${LANDING_BASE_URL}/docs/runtime-apis` },
  { labelKey: 'footer.links.status', href: 'https://status.bundledrop.app' },
  { labelKey: 'footer.links.support', href: `${LANDING_BASE_URL}/contact` },
  { labelKey: 'footer.links.pricing', href: `${LANDING_BASE_URL}/pricing` },
] as const;

const legalLinks = [
  { labelKey: 'footer.links.privacy', href: `${LANDING_BASE_URL}/privacy-policy` },
  { labelKey: 'footer.links.terms', href: `${LANDING_BASE_URL}/terms-of-service` },
  { labelKey: 'footer.links.refund', href: `${LANDING_BASE_URL}/refund` },
] as const;

type FooterProps = {
  fixed?: boolean;
  transparent?: boolean;
};

const FooterLinkGroup = ({
  ariaLabel,
  className,
  links,
  trailingAction,
}: {
  ariaLabel: string;
  className?: string;
  links: readonly { labelKey: string; href: string }[];
  trailingAction?: { label: string; onClick: () => void };
}) => {
  const { t } = useTranslation();

  return (
    <nav className={`${styles.nav}${className ? ` ${className}` : ''}`} aria-label={ariaLabel}>
      {links.map(({ labelKey, href }, index) => (
        <Fragment key={href}>
          {index > 0 ? (
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
          ) : null}
          <a key={href} className={styles.link} href={href} target="_blank" rel="noreferrer">
            {t(labelKey)}
          </a>
        </Fragment>
      ))}
      {trailingAction ? (
        <>
          <span className={styles.separator} aria-hidden="true">
            ·
          </span>
          <button type="button" className={styles.linkButton} onClick={trailingAction.onClick}>
            {trailingAction.label}
          </button>
        </>
      ) : null}
    </nav>
  );
};

const Footer = ({ fixed = false, transparent = false }: FooterProps) => {
  const { colors } = useTheme();
  const { canOfferAnalytics, openPreferences } = useAnalyticsConsent();
  const { t } = useTranslation();
  const footerStyle = {
    '--footer-link-color': colors.text_400,
    '--footer-link-hover-color': colors.text_500,
    '--footer-separator-color': colors.text_400,
    background: transparent ? 'transparent' : colors.bg_500,
    color: colors.text_400,
    textAlign: 'left',
    padding: '0 24px',
  } as CSSProperties;

  return (
    <AntFooter
      className={`${styles.footer}${fixed ? ` ${styles.fixed}` : ''}`}
      style={footerStyle}
      data-testid="layout-footer"
    >
      <div className={styles.inner}>
        <FooterLinkGroup
          ariaLabel={t('footer.productNavLabel')}
          className={styles.productNav}
          links={productLinks}
        />
        <span className={styles.copy}>{t('footer.copy', { year: new Date().getFullYear() })}</span>
        <FooterLinkGroup
          ariaLabel={t('footer.legalNavLabel')}
          className={styles.legalNav}
          links={legalLinks}
          trailingAction={
            canOfferAnalytics
              ? {
                  label: t('analyticsConsent.manage'),
                  onClick: openPreferences,
                }
              : undefined
          }
        />
      </div>
    </AntFooter>
  );
};

export default Footer;
